import React, { useEffect, useState } from 'react';
import 'react-infinite-calendar/styles.css';
import ProviderRequestSuccessDialog from './components/ProviderRequestSuccessDialog';
import JobDescriptionPage from './components/pages/JobDescriptionPage';
import DateAndTimePage from './components/pages/DateAndTimePage';
import PayPage from './components/pages/PayPage';
import ProviderPreferencesPage from './components/pages/ProviderPreferencesPage/ProviderPreferencesPage';
import { JobFormContextProvider, useJobFormContext } from './JobFormContext';
import { useHistory } from 'react-router';
import HeadlineDescriptionPage from './components/pages/HeadlineDescriptionPage/HeadlineDescriptionPage';
import { KidPetContextProvider } from './contexts/KidPetContext';
import { JobTypesWithDependents } from 'models/JobType';
import { useUserContext } from 'UserContext';
import TandemPayPage from './components/pages/TandemPayPage';
import useSignUpAction from 'parent-portal/login/SignUpAction';
import JobSuccessPage from './components/pages/ConfirmationPage/JobSuccessPage';
import { SizeableRoundedDialog as Dialog } from 'library';
import { useMediaQuery } from '@material-ui/core';
import theme from 'theme';
import { useModalsContext } from 'ModalsContext';
import OneTimeJobDetailsPage from './components/pages/SimpleJobForm/OneTimeJobDetailsPage';
import { isLoggedIn } from 'api/UserApi';

enum ConditionalPage {
    dependents = 'dependents',
    userInfo = 'userInfo',
    tandemPayAgreement = 'tandemPayAgreement',
    providerPreferences = 'providerPreferences',
    longFormPages = 'longFormPages',
}

function JobRequestForm() {
    const {
        currentPage,
        form,
        createdJob,
        invitedProvider,
        setPageCount,
        pastProviders,
        preferredProviders,
        previouslyAgreedToTandemPay,
        jobTypes,
        reset,
        simple,
    } = useJobFormContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const xs = useMediaQuery(theme.breakpoints.only('xs'));
    const { fieldStates } = form;
    const { user } = useUserContext();
    const [completedProviderRequest, setCompletedProviderRequest] = useState(false);
    const [conditionalPagesToInclude, setConditionalPagesToInclude] = useState<ConditionalPage[]>([]);
    const history = useHistory();
    const { SignUpWrapper, signUp } = useSignUpAction(() => history.push('/jobs'));
    const { jobFormOpen, closeJobForm } = useModalsContext();

    useEffect(() => {
        const includeDependentsPage = JobTypesWithDependents.includes(fieldStates.jobTypeId.value);
        const includeTandemPayAgreementPage = !previouslyAgreedToTandemPay;
        const jobTypeName = jobTypes.find((x) => x.id === fieldStates.jobTypeId.value)?.name ?? 'other';
        const childCareJob = jobTypeName === 'Child Care';
        const showProviderPreferences =
            isLoggedIn() &&
            (childCareJob ||
                pastProviders.length > 0 ||
                preferredProviders.length > 0 ||
                fieldStates.requestType.value === 'recurring');

        let conditionalPages = [
            includeDependentsPage && ConditionalPage.dependents,
            includeTandemPayAgreementPage && ConditionalPage.tandemPayAgreement,
            showProviderPreferences && ConditionalPage.providerPreferences,
            !simple && ConditionalPage.longFormPages,
        ].filter((pageApplies) => !!pageApplies) as ConditionalPage[];

        setConditionalPagesToInclude(conditionalPages);
    }, [fieldStates.isQuickFill.value, fieldStates.jobTypeId.value, previouslyAgreedToTandemPay, simple]);

    useEffect(() => {
        const addresses = user?.address;
        if (addresses?.length) {
            const mailingAddress = addresses.find((x) => x.address_type === 'PRIMARY');
            const addressId = mailingAddress ? mailingAddress.id : addresses[0].id;

            form.fieldStates.address.setValue([addressId]);
        }
    }, [user]);

    function completeFollowUps() {
        if (!!user?.first_name) {
            close();
            if (history.location.pathname.includes('/jobs')) history.go(0);
        } else {
            signUp(`${fieldStates.requestType.value} Job Form`);
        }
    }

    function close() {
        reset();
        closeJobForm();
    }

    function shouldInclude(page: ConditionalPage) {
        return conditionalPagesToInclude.includes(page);
    }

    const pages = [
        <JobDescriptionPage fieldStates={fieldStates} />,
        shouldInclude(ConditionalPage.longFormPages) ? <DateAndTimePage fieldStates={fieldStates} /> : null,
        shouldInclude(ConditionalPage.longFormPages) ? (
            <HeadlineDescriptionPage
                showDependents={shouldInclude(ConditionalPage.dependents)}
                fieldStates={fieldStates}
            />
        ) : null,
        shouldInclude(ConditionalPage.longFormPages) && shouldInclude(ConditionalPage.providerPreferences) ? (
            <ProviderPreferencesPage fieldStates={fieldStates} />
        ) : null,
        shouldInclude(ConditionalPage.tandemPayAgreement) ? <TandemPayPage fieldStates={fieldStates} /> : null,
        shouldInclude(ConditionalPage.longFormPages) ? <PayPage fieldStates={fieldStates} /> : null,
        !shouldInclude(ConditionalPage.longFormPages) ? <OneTimeJobDetailsPage fieldStates={fieldStates} /> : null,
        <JobSuccessPage job={createdJob} onClose={completeFollowUps} />,
    ].filter((page) => page !== null);

    useEffect(() => {
        setPageCount(pages.length);
    }, [pages]);

    return (
        <Dialog
            open={jobFormOpen}
            onClose={createdJob ? completeFollowUps : close}
            closeButton
            style={{ width: isMobile ? undefined : '60%', maxWidth: 643, maxHeight: '95vh' }}
            contentStyle={{ marginTop: 20 }}
            fullScreen={xs}
        >
            {pages[currentPage]}
            {createdJob && invitedProvider ? (
                <ProviderRequestSuccessDialog
                    provider={invitedProvider}
                    open={!completedProviderRequest}
                    onClose={() => setCompletedProviderRequest(true)}
                    next={!fieldStates.isQuickFill.value}
                    nextAction={() => setCompletedProviderRequest(true)}
                    page="One Time"
                    jobRequest={createdJob}
                    location={`${fieldStates.requestType.value} Job Form`}
                />
            ) : null}
            <SignUpWrapper />
        </Dialog>
    );
}

export default function JobFormWithContext() {
    const { role } = useUserContext();
    if (role && role !== 'admin') {
        return (
            <JobFormContextProvider>
                <KidPetContextProvider>
                    <JobRequestForm />
                </KidPetContextProvider>
            </JobFormContextProvider>
        );
    }
    return null;
}
